import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Line } from 'react-chartjs-2'
import { Chart as ChartJS, registerables } from 'chart.js'
import { Typography } from '@mui/material'
import { Modal } from 'frame/components'
import { themeV5 } from 'theme/v5'
import zoomPlugin from 'chartjs-plugin-zoom'

ChartJS.register(...registerables, zoomPlugin)

const LineChart = ({
  data,
  onClick,
  onLegendCountClick,
  noDataMessage,
  showHealthAndSafetyDisclaimer,
}) => {
  const [openDisclaimerMessage, setOpenDisclaimerMessage] = useState(false)
  const [activeIndex, setActiveIndex] = useState(null)

  const chartRef = useRef(null)

  const getGraphData = () => ({
    labels: data.labels,
    datasets: (data?.datasets ?? []).map((ds, i) => ({
      label: ds.label,
      data: ds.data ?? [],
      borderColor: ds.color,
      backgroundColor: ds.color,
      fill: false,
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.3,
      spanGaps: false,
      totalCount: ds.totalCount,
    })),
  })

  const graph = getGraphData()

  const lastIndex = useMemo(() => {
    if (Array.isArray(data?.labels) && data.labels.length > 0) {
      return data.labels.length - 1
    }
    return null
  }, [data?.labels])

  useEffect(() => {
    setActiveIndex(null)
  }, [lastIndex])

  const handleHover = useCallback(
    (_, chartElements) => {
      if (!Array.isArray(chartElements) || chartElements.length === 0) {
        setActiveIndex(null)
        return
      }

      const { index } = chartElements[0] ?? {}
      if (typeof index === 'number' && index !== activeIndex) {
        setActiveIndex(index)
      }
    },
    [activeIndex, lastIndex],
  )

  const CustomLegend = useMemo(() => {
    if (!graph?.datasets) {
      return null
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 12,
          gap: 40,
        }}>
        {graph.datasets.map((ds, idx) => {
          const hoveredValue =
            typeof activeIndex === 'number' && Array.isArray(ds.data)
              ? ds.data[activeIndex]
              : ds.totalCount ?? null
          const baseLabel = (ds.legendBaseLabel || ds.label || '').replace(/\s*\(.*\)$/, '').trim()
          const hasCount = hoveredValue !== null && hoveredValue !== undefined
          const isClickable = typeof onLegendCountClick === 'function'

          return (
            <span
              key={idx}
              onClick={
                isClickable
                  ? () => onLegendCountClick(baseLabel)
                  : undefined
              }
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: ds.borderColor,
                cursor: isClickable ? 'pointer' : 'default',
              }}>
              {baseLabel}
              {hasCount && (
                <span style={{ marginLeft: 4, textDecoration: isClickable ? 'underline' : 'none' }}>
                  ({hoveredValue})
                </span>
              )}
            </span>
          )
        })}
      </div>
    )
  }, [graph.datasets, activeIndex])

  const isInsideChartArea = (chart, event) => {
    if (!chart || !chart.chartArea) {
      return false
    }

    const rect = chart.canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const { left, right, top, bottom } = chart.chartArea

    return x >= left && x <= right && y >= top && y <= bottom
  }

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) {
      return
    }

    const canvas = chart.canvas
    let draggingFromInside = false

    const isInside = (e) => {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      const { left, right, top, bottom } = chart.chartArea
      return x >= left && x <= right && y >= top && y <= bottom
    }

    const onMouseDown = (e) => {
      draggingFromInside = isInside(e)

      if (!draggingFromInside) {
        e.stopImmediatePropagation()
        e.preventDefault()
      }
    }

    const onMouseMove = (e) => {
      if (draggingFromInside && !isInside(e)) {
        e.stopImmediatePropagation()
        e.preventDefault()

        canvas.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }))
        draggingFromInside = false
      }
    }

    const onMouseUp = () => {
      draggingFromInside = false
    }

    canvas.addEventListener('mousedown', onMouseDown, true)
    canvas.addEventListener('mousemove', onMouseMove, true)
    canvas.addEventListener('mouseup', onMouseUp, true)

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown, true)
      canvas.removeEventListener('mousemove', onMouseMove, true)
      canvas.removeEventListener('mouseup', onMouseUp, true)
    }
  }, [])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 0
      }
    },
    scales: {
      x: {
        ticks: {
          padding: 20,
          color: themeV5.palette.graph.secondary,
        },
      },
      y: {
        ticks: {
          beginAtZero: true,
          padding: 20,
          precision: 0,
          color: themeV5.palette.graph.secondary,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: true,
        callbacks: {
          label: (tooltipItem) => ` ${tooltipItem.parsed.y}`,
        },
      },
      zoom: {
        pan: { enabled: false },
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: 'rgba(0,123,255,0.2)',
            borderColor: 'rgba(0,123,255,0.8)',
            borderWidth: 1,
          },
          mode: 'xy',
          onZoomStart ({ chart, event }) {
            return isInsideChartArea(chart, event)
          },
        },
      },
    },
    onClick,
    onHover: handleHover,
  }

  const hasData = data.datasets?.some(
    (ds) => Array.isArray(ds.data) && ds.data.some((v) => typeof v === 'number'),
  )

  return (
    <>
      {hasData ? (
        <div style={{ height: '230px', position: 'relative' }}>
          <Line
            height={140}
            data={graph}
            options={options}
            ref={chartRef}/>
          {CustomLegend}
        </div>
      ) : (
        <Typography variant="body1">
          {noDataMessage || 'No data available'}

          {showHealthAndSafetyDisclaimer && (
            <>
              <br />
              <span
                style={{
                  color: themeV5.palette.text.link,
                  cursor: 'pointer',
                }}
                onClick={() => setOpenDisclaimerMessage(true)}>
                View Health and Safety Disclaimer
              </span>
            </>
          )}
        </Typography>
      )}

      {openDisclaimerMessage && (
        <Modal
          open={openDisclaimerMessage}
          title="Health and Safety Disclaimer"
          onClose={() => setOpenDisclaimerMessage(false)}>
          Please note there are possible implications to your obligations both
          under your finance contract/lease and the Health and Safety at Work
          Act 2015 if the servicing requirements are not completed when due.
        </Modal>
      )}
    </>
  )
}

LineChart.propTypes = {
  data: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  onLegendCountClick: PropTypes.func,
  noDataMessage: PropTypes.string,
  showHealthAndSafetyDisclaimer: PropTypes.bool,
}

export default LineChart
